import { useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { Drawer, Avatar, Button, DrawerProps } from '@material-ui/core'
import { faBars, faComments, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'

import Fbar from '../Fbar/Fbar'
import Hbar, {
    LeadingArea,
    CoreArea,
    FollowingArea,
    BreakingLink,
} from '../Hbar/Hbar'
import bgImage from '../../images/bg/rebel.png'
import links from '../../views/headerLinks'
/* import lStyles from './layout.module.css' */
import hStyles from '../Hbar/hbar.module.css'
import { LogoArea } from 'components'

const useStyles = makeStyles((theme) => ({
    out: {
        backgroundColor: '#000004',
        color: 'ghostwhite',
        background: `url("${bgImage}") repeat`,
        minHeight: '100vh',
    },
    header: {
        minHeight: '2rem',
        '& > .App-logo': {
            height: '1.5rem',
            width: '1.5rem',
        },
    },
    horizon: {
        height: '90%',
    },
    superNav: {
        gridColumnEnd: 'span 2',
    },
    core: {
        gridColumnEnd: 'span 8',
        height: '100%',
        minHeight: '80vh',
    },
    superAside: {
        gridColumnEnd: 'span 2',
    },
    homeStar: {
        marginRight: theme.spacing(1),
    },
}))

interface PrimaryDrawerProps extends DrawerProps {
    open: boolean
    toggle?: Boolean
    children?: React.ReactNode
    className?: string
}

const PrimaryDrawer = ({
    open,
    toggle,
    children,
    className,
    ...rest
}: PrimaryDrawerProps) => {
    return (
        <Drawer anchor="left" open={open} {...rest}>
            <div className={className}>{children}</div>
        </Drawer>
    )
}

interface SecondaryDrawerProps {
    open: boolean
    onClose: (e: any) => void
    toggle?: boolean
    children: React.ReactNode
    className?: string
}

/**
 * The Secondary drawer is open until force closed by Xing out
 */
const SecondaryDrawer = ({
    open,
    toggle,
    children,
    className,
    onClose,
    ...rest
}: SecondaryDrawerProps) => {
    return (
        <Drawer
            anchor="right"
            className="secondary-drawer"
            open={open}
            {...rest}
        >
            <Button onClick={onClose}>
                <Icon icon={faTimes} />
            </Button>
            <aside className={className}>{children}</aside>
        </Drawer>
    )
}

interface LayoutProps {
    profile:
        | {
              avatarUrl: string
              name: string
          }
        | {}
    children: React.ReactNode
    className?: string
    open?: boolean
    navContent: React.ReactNode
    asideContent: React.ReactNode
}

/**
 * General 1-column layout with side drawers
 * Header HBar area also includes 3 sections for leading hamburger nav, core links /logo / search, and profile / chat
 * @param {*} props
 */
const Layout = ({
    navContent,
    asideContent,
    profile = {},
    open,
    className,
    children,
}: LayoutProps) => {
    const classes = useStyles()
    const history = useHistory()
    // Programmatically go to a url
    const navigate = (url:string)=>{
        history.push(url)
    }
    const [navOpen, setNavOpen] = useState(false)
    const [profileOpen, setProfileOpen] = useState(false)
    const [asideOpen, setAsideOpen] = useState(false)
    // @ts-ignore
    const { avatarUrl, name } = profile
    const initial = (word: string) => {
        return word ? word.charAt(0).toUpperCase() : ''
    }
    // Close this nav on any non-navigational keypress or click
    const toggleNav =
        (openN: boolean) => (event: React.MouseEvent | React.KeyboardEvent) => {
            if (
                event.type === 'keydown' && // @ts-ignore
                (event.key === 'Tab' || event.key === 'Shift')
            ) {
                return
            }
            setNavOpen(!openN)
        }
    // Close the chat only when X clicked
    const closeAside = () => {
        setAsideOpen(false)
    }
    // @ts-ignore
    return (
        <div className={`${classes.out} ${className}`}>
            <Hbar role='banner' className={classes.header}>
                <LeadingArea
                    onClick={() => {
                        setNavOpen(!navOpen)
                    }}
                >
                    <Icon className={hStyles['h-icon']} icon={faBars} />
                </LeadingArea>
                <CoreArea>
                    <Link to="/">
                        <LogoArea
                            className={classes.homeStar}
                            shrinkable
                            title="NinjaWars"
                        />
                    </Link>{' '}
                    <span className={hStyles['link-list']}>
                        {links.map((link) => (
                            <BreakingLink key={link.url} link={link} />
                        ))}
                    </span>
                </CoreArea>
                <FollowingArea>
                    <Button
                        onClick={(e) => {
                            setProfileOpen(!profileOpen)
                            navigate('/me')
                        }}
                    >
                        <Avatar src={avatarUrl} alt={initial(name)}>
                            {initial(name)}
                        </Avatar>
                    </Button>
                    <Button
                        onClick={(e) => {
                            setAsideOpen(!asideOpen)
                        }}
                    >
                        <Icon className={hStyles['h-icon']} icon={faComments} />
                    </Button>
                </FollowingArea>
            </Hbar>
            <div className={classes.horizon}>
                <PrimaryDrawer
                    open={open || navOpen}
                    onClick={toggleNav(navOpen)}
                    onKeyDown={toggleNav(navOpen)}
                    className={classes.superNav}
                >
                    {navContent}
                </PrimaryDrawer>
                <main className={classes.core}>{children}</main>
                <SecondaryDrawer
                    open={open || asideOpen}
                    onClose={(e) => {
                        closeAside()
                    }}
                    className={classes.superAside}
                >
                    {asideContent}
                </SecondaryDrawer>
            </div>
            <Fbar />
        </div>
    )
}

export default Layout
